// src/hooks/useDragBatch.js
import React, { useRef, useEffect } from "react";
import { Container, Draggable } from "react-smooth-dnd";
import arrayMove from "array-move";

/**
 * useDragBatch
 * - dragClass: string (selector class for drag handle)
 * - items: array (current items state)
 * - setItems: function to set items state
 * - onReorder: async function(payload) -> Promise  // payload = { course, order: [{id,order}, ...] }
 * - opts: { debounceMs: number, reload: ()=>void (optional) }
 */
const useDragBatch = (
  dragClass,
  items,
  setItems,
  onReorder,
  opts = { debounceMs: 250, reload: () => {} }
) => {
  const debounceTimerRef = useRef(null);
  const pendingRequestRef = useRef(null);
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const triggerReorder = (orderedPayload, course) => {
    // debounce single backend call
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(async () => {
      // avoid parallel requests: wait previous to finish
      if (pendingRequestRef.current) {
        try {
          await pendingRequestRef.current;
        } catch (e) {
          // ignore
        }
      }
      // call onReorder; keep ref to the promise
      pendingRequestRef.current = onReorder({ course, order: orderedPayload });

      try {
        await pendingRequestRef.current;
        // optional reload if provided
        opts.reload && opts.reload();
      } catch (err) {
        // caller should handle errors; here we just console
        console.error("Reorder request failed", err);
      } finally {
        pendingRequestRef.current = null;
        debounceTimerRef.current = null;
      }
    }, opts.debounceMs || 250);
  };

  const handleDrop = (dropResult, extra = {}) => {
    // dropResult has removedIndex, addedIndex
    if (dropResult && (dropResult.removedIndex === null && dropResult.addedIndex === null)) {
      return;
    }

    const removedIndex = dropResult.removedIndex;
    const addedIndex = dropResult.addedIndex;
    const newRows = arrayMove(itemsRef.current, removedIndex, addedIndex);

    // only proceed if changed
    if (JSON.stringify(newRows) !== JSON.stringify(itemsRef.current)) {
      // optimistic update
      setItems(newRows);

      // prepare ordered payload: array of {id, order}
      const orderedPayload = newRows.map((m, i) => ({ id: m.id, order: i + 1 }));

      // course id must be provided by caller via extra.course or inside items
      const course = extra.course || (newRows[0] && newRows[0].course_id) || null;
      triggerReorder(orderedPayload, course);
    }
  };

  const DropContainer = ({ children }) => (
    <Container
      lockAxis="y"
      dragHandleSelector={"." + dragClass}
      onDrop={(dropResult) => handleDrop(dropResult)}
      dropPlaceholder={{
        animationDuration: 150,
        showOnTop: true,
      }}
      dropPlaceholderAnimationDuration={200}
    >
      {children}
    </Container>
  );

  const DropElement = ({ children }) => (
    <Draggable>
      {children}
    </Draggable>
  );

  return {
    DropContainer,
    DropElement,
    dragClass,
    handleDrop,
  };
};

export default useDragBatch;
